'use client';

import { createPortal } from 'react-dom';
import { cn } from '../lib/cn';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { useModalStack, type ModalZIndex } from './ModalStackProvider';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full' | 'none';
type ModalVariant = 'centered' | 'bottomSheet';

type BaseModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /**
   * false면 backdrop 클릭과 native dialog의 ESC 닫기를 차단한다.
   * 처리 중 데이터 손실을 막을 때 사용. default true.
   */
  canClose?: boolean;
  /**
   * true면 모달을 화면에서 숨기지만 unmount 하지는 않는다(display:none).
   * isOpen 상태와 모든 child 상태를 보존하면서 다른 모달이 위에 떴을 때 시각 충돌을 피할 때 사용.
   */
  isHidden?: boolean;
  variant?: ModalVariant;
  /**
   * true면 폭과 무관하게 항상 화면 중앙 모달로 렌더한다(바텀시트 비활성).
   * 데스크탑 앱 등 모바일 폭이어도 바텀시트를 쓰지 않으려는 경우에 사용. default false.
   */
  forceCentered?: boolean;
  size?: ModalSize;
  /**
   * z-index 오버라이드(특수 케이스 전용).
   *
   * 기본값 `base` 에서는 `ModalStackProvider` 가 열림 순서대로 z-index 를 자동 배정하므로
   * 호출처는 중첩 스태킹을 신경 쓸 필요가 없다(나중에 연 모달이 자동으로 위에 온다).
   * "항상 최상단" 같은 예외가 필요할 때만 `overlay`~`top` 단계를 명시한다.
   * Provider 미마운트 환경(SSR/단위 테스트)에서는 이 값이 `zIndexMap` 클래스로 폴백된다.
   */
  zIndex?: ModalZIndex;
  /**
   * true면 최상위 모달(depth 0)이어도 body로 portal한다.
   * 조상에 `opacity`/`transform`이 걸린 곳(딤 처리된 카드 등)에서 모달을 여는 호출부용 —
   * 그대로 두면 모달과 backdrop이 함께 흐려지고 그 조상의 stacking context에 갇힌다.
   */
  portal?: boolean;
  height?: string;
  className?: string;
  backdropClassName?: string;
  children: React.ReactNode;
};

const sizeMap: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  full: 'max-w-full',
  none: 'max-w-none',
};

const mdSizeMap: Record<ModalSize, string> = {
  sm: 'md:max-w-sm',
  md: 'md:max-w-md',
  lg: 'md:max-w-lg',
  xl: 'md:max-w-xl',
  '2xl': 'md:max-w-2xl',
  '3xl': 'md:max-w-3xl',
  '4xl': 'md:!max-w-4xl',
  '5xl': 'md:max-w-5xl',
  full: 'md:max-w-full',
  none: 'md:max-w-none',
};

const zIndexMap: Record<ModalZIndex, string | undefined> = {
  base: undefined,
  overlay: 'z-[1000]',
  dropdown: 'z-[1010]',
  popover: 'z-[1020]',
  top: 'z-[1030]',
};

export const BaseModal = ({
  isOpen,
  onClose,
  canClose = true,
  isHidden = false,
  variant = 'centered',
  forceCentered = false,
  size = '2xl',
  zIndex = 'base',
  portal = false,
  height,
  className,
  backdropClassName,
  children,
}: BaseModalProps) => {
  useBodyScrollLock(isOpen && !isHidden);
  const stackInfo = useModalStack(isOpen && !isHidden, zIndex);
  if (!isOpen) return null;

  const isCentered = variant === 'centered';

  const autoZIndex = stackInfo !== null ? stackInfo.zIndex : null;
  const zIndexClassName = autoZIndex === null ? zIndexMap[zIndex] : undefined;

  const dialogStyle: React.CSSProperties = {};
  if (isHidden) dialogStyle.display = 'none';
  if (autoZIndex !== null) dialogStyle.zIndex = autoZIndex;

  const modalBoxClassName = isCentered
    ? cn(
        'modal-box p-0 overflow-hidden',
        forceCentered
          ? cn('w-11/12 rounded-2xl', sizeMap[size])
          : cn('w-full max-w-full rounded-t-2xl rounded-b-none md:w-11/12 md:rounded-2xl', mdSizeMap[size]),
        height,
        className,
      )
    : cn(
        'modal-box w-full max-w-full md:w-11/12 p-5 pb-[calc(env(safe-area-inset-bottom)+1.25rem)] md:rounded-2xl md:p-6 md:pb-6',
        forceCentered ? 'rounded-2xl' : 'rounded-t-2xl',
        mdSizeMap[size],
        height,
        className,
      );

  const depth = stackInfo !== null ? stackInfo.depth : 0;
  const backdropBaseClass = depth === 0 ? 'bg-black/30 backdrop-blur-xs' : 'bg-black/5';

  const dialog = (
    <dialog
      open
      onCancel={(event) => {
        event.preventDefault();
        if (canClose) onClose();
      }}
      style={Object.keys(dialogStyle).length > 0 ? dialogStyle : undefined}
      className={cn('modal', forceCentered ? 'modal-middle' : 'modal-bottom md:modal-middle', zIndexClassName)}
    >
      <div className={modalBoxClassName}>{children}</div>
      <form
        method="dialog"
        className={cn('modal-backdrop', backdropBaseClass, backdropClassName)}
        onSubmit={(event) => {
          event.preventDefault();
          if (!canClose) return;
          onClose();
        }}
      >
        <button type="submit">close</button>
      </form>
    </dialog>
  );

  /*
   * 중첩 모달은 부모 모달의 children으로 선언되므로 DOM에서도 부모 `.modal-box` 안에 들어간다.
   * daisyUI의 `.modal-box`는 열림 애니메이션 때문에 CSS `translate`를 갖는데, 그러면 그 안의
   * `position: fixed` 자손은 뷰포트가 아니라 **박스**를 기준으로 자리를 잡고 박스의
   * `overflow: hidden`에 잘린다(실측: body/dialog 안의 fixed는 (0,0), modal-box 안에서는 박스 원점).
   * `translate`는 `getComputedStyle().transform`에 안 잡혀서 눈으로 찾기 어려운 종류의 함정이라,
   * 호출처가 알아서 피하게 두지 않고 여기서 body로 portal해 사슬 자체를 끊는다.
   *
   * depth 0(최상위 모달)은 지금까지와 동일하게 제자리에 렌더한다. 단, 조상에 opacity/transform이
   * 걸린 자리에서 여는 호출부는 같은 이유로 깨지므로 `portal`로 명시해 탈출할 수 있다.
   */
  if ((depth > 0 || portal) && typeof document !== 'undefined') {
    return createPortal(dialog, document.body);
  }

  return dialog;
};

export type { ModalSize, ModalVariant, BaseModalProps, ModalZIndex };
